type Entry = {
  sector: string
  outcome: string
  bullets: string[]
  scope: string[]
}

const entries: Entry[] = [
  {
    sector: 'Fintech operations',
    outcome:
      'Unified ops console replacing three legacy dashboards for a regional payments provider.',
    bullets: [
      'Role-based workflow engine with audit trails and maker-checker approvals',
      'Real-time settlement monitoring with configurable alert thresholds',
      'Integration layer across core banking APIs and internal risk scoring',
    ],
    scope: ['Workflow engine', 'Audit trail', 'Core banking integration'],
  },
  {
    sector: 'Logistics control tower',
    outcome:
      'Single pane for fleet dispatch, warehouse throughput, and customer SLA tracking.',
    bullets: [
      'Event-driven dashboard aggregating GPS, WMS, and TMS data streams',
      'Exception routing with automated escalation to on-call dispatchers',
      'Predictive delay scoring using historical route and weather patterns',
    ],
    scope: ['Event pipeline', 'Exception routing', 'Delay scoring'],
  },
  {
    sector: 'Healthcare scheduling',
    outcome:
      'Patient booking platform with staff roster optimization for a multi-clinic network.',
    bullets: [
      'Self-service portal with insurance verification and waitlist management',
      'AI-assisted slot allocation respecting clinician specialties and room capacity',
      'HL7/FHIR adapters for EHR synchronization without duplicate records',
    ],
    scope: ['Patient portal', 'Slot allocation', 'HL7/FHIR adapters'],
  },
  {
    sector: 'B2B marketplace',
    outcome:
      'Procurement marketplace connecting industrial buyers with vetted regional suppliers.',
    bullets: [
      'Multi-tenant catalog with negotiated pricing tiers and contract enforcement',
      'RFQ workflow with document exchange and approval chains',
      'Payment orchestration with escrow release tied to delivery confirmation',
    ],
    scope: ['Multi-tenant catalog', 'RFQ workflow', 'Payment orchestration'],
  },
  {
    sector: 'Internal AI tooling',
    outcome:
      'Knowledge retrieval and drafting assistant embedded in a professional services firm’s daily workflow.',
    bullets: [
      'Secure retrieval pipeline over client matter files, mirroring document-system permissions',
      'Template-aware drafting with human review before client delivery',
      'Quality feedback loop for continuous refinement',
    ],
    scope: ['Retrieval pipeline', 'Access control', 'Review loop'],
  },
]

/**
 * Observation register. Sits directly under the title sheet so its header and
 * first rules cut the bottom of the first viewport — the contract's defining move.
 */
export function Register() {
  return (
    <section id="register" className="bg-sheet">
      <div className="mx-auto max-w-[100rem] px-6 pt-6 pb-12 lg:px-8 lg:pt-7 lg:pb-16">
        <div className="cell overflow-hidden">
          <div className="flex flex-col gap-2 border-b-2 border-ink px-5 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-7">
            <h2 className="section-title text-ink">Selected engagements</h2>
            <p className="text-sm leading-relaxed text-ink-muted sm:max-w-xs sm:text-right">
              Representative work, anonymized. Details available under NDA.
            </p>
          </div>

          <div className="hidden grid-cols-[12rem_1fr_14rem] gap-6 border-b border-rule bg-sheet-edge/60 px-5 py-2.5 sm:px-7 lg:grid">
            <span className="cell-label text-ink-muted">Sector</span>
            <span className="cell-label text-ink-muted">Observation</span>
            <span className="cell-label text-ink-muted">Scope delivered</span>
          </div>

          <div className="divide-y divide-rule">
            {entries.map((entry) => (
              <article
                key={entry.sector}
                className="grid gap-4 px-5 py-5 sm:px-7 lg:grid-cols-[12rem_1fr_14rem] lg:gap-6 lg:py-6"
              >
                <h3
                  className="entry-title text-ink"
                  style={{ fontStretch: '80%' }}
                >
                  {entry.sector}
                </h3>

                <div>
                  <p className="text-base font-medium leading-snug text-ink">
                    {entry.outcome}
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {entry.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex gap-3 text-[0.9375rem] leading-relaxed text-ink-muted"
                      >
                        <span
                          className="mt-2.5 h-px w-3 shrink-0 bg-overprint"
                          aria-hidden="true"
                        />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>

                <ul className="space-y-1 lg:pt-0.5">
                  {entry.scope.map((tag) => (
                    <li key={tag} className="cell-label text-ink-muted">
                      {tag}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
